import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useEffect, useState } from "react";

import { AvatarProfile, SingleSelectInput } from "@performyard/py-components";
import type { InputSize } from "@performyard/py-components/src/components/atoms/InputBase";
import type { Option } from "@performyard/py-components/src/types/Option";

import { EmployeeValue, createFetchEmployeesForPicker } from "./shared";

export type SingleSelectEmployeeProps = {
	size?: InputSize;
	value?: EmployeeValue | null;
	onChange: (employeeId: string | null, employeeName: string | null) => void;
	fetchEmployees?: (searchTerm: string) => Promise<{ id: string; name: string; label?: string }[]>;
	errored?: boolean;
	disabled?: boolean;
	placeholder?: string;
	id?: string;
	"data-testid"?: string;
	isClearable?: boolean;
	dangerouslySetClassName?: string;
	minOptionsForCombobox?: number;
	ariaLabel?: string;
};

export const Single = ({
	size,
	value,
	onChange,
	fetchEmployees: customFetchEmployees,
	errored,
	disabled,
	placeholder = "Search for an employee...",
	id,
	"data-testid": dataTestId,
	isClearable = true,
	dangerouslySetClassName,
	minOptionsForCombobox,
	ariaLabel,
}: SingleSelectEmployeeProps) => {
	const queryClient = useQueryClient();
	const [selectedEmployee, setSelectedEmployee] = useState<EmployeeValue | undefined>(value ?? undefined);

	const fetchEmployeeOptions = useCallback(
		async (searchTerm: string): Promise<Option<EmployeeValue>[]> => {
			try {
				let employees: { id: string; name: string; label?: string }[];

				if (customFetchEmployees) {
					employees = await customFetchEmployees(searchTerm);
				} else {
					const defaultFetch = createFetchEmployeesForPicker(queryClient);
					employees = await defaultFetch(searchTerm);
				}

				return employees.map((employee) => ({
					label: employee.label ?? employee.name,
					value: {
						id: employee.id,
						name: employee.name,
					},
				}));
			} catch (error) {
				console.error("Error fetching employees:", error);
				return [];
			}
		},
		[queryClient, customFetchEmployees]
	);

	useEffect(() => {
		if (!value) {
			setSelectedEmployee(undefined);
			return;
		}

		if (selectedEmployee?.id === value.id) {
			return;
		}

		setSelectedEmployee(value);
	}, [value, selectedEmployee]);

	const handleChange = (newValue: EmployeeValue | null) => {
		setSelectedEmployee(newValue ?? undefined);
		if (newValue) {
			onChange(newValue.id, newValue.name);
		} else {
			onChange(null, null);
		}
	};

	const textSize = size === "small" ? "caption" : "paragraph-3";

	const renderEmployeeValue = useCallback(
		(value: EmployeeValue) => (
			<AvatarProfile employeeId={value.id} employeeName={value.name} avatarSize={20} textSize={textSize} noLink />
		),
		[textSize]
	);

	const renderEmployeeOption = useCallback(
		(option: Option<EmployeeValue>) => (
			<AvatarProfile
				employeeId={option.value.id}
				employeeName={option.value.name}
				avatarSize={20}
				textSize={textSize}
				noLink
			/>
		),
		[textSize]
	);

	return (
		<SingleSelectInput<EmployeeValue>
			size={size}
			fetchOptions={fetchEmployeeOptions}
			value={selectedEmployee}
			onChange={handleChange}
			selectionVariant="combobox"
			placeholder={placeholder}
			errored={errored}
			disabled={disabled}
			id={id}
			data-testid={dataTestId}
			isClearable={isClearable}
			renderValue={renderEmployeeValue}
			renderOption={renderEmployeeOption}
			dangerouslySetClassName={dangerouslySetClassName}
			minOptionsForCombobox={minOptionsForCombobox}
			ariaLabel={ariaLabel}
		/>
	);
};

Single.displayName = "SingleSelectEmployee";
